import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";

const INITIAL_DATA = {
  name: "",
  email: "",
  phoneNumber: "",
  designation: "",
  gender: "",
  dateOfBirth: "",
  address: "",
  nationality: "Indian",
  assignedBranch: "",
};

const GENDERS = ["male", "female", "other"];

const AddStaffDrawer = ({ isOpen, onClose, onSuccess, editingStaff }) => {
  const { isAdmin, admin } = useAdminAuth();
  const isEditMode = Boolean(editingStaff);

  const [formData, setFormData] = useState(INITIAL_DATA);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !isAdmin) return;

    const fetchBranches = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/branches/get-all`,
        );

        setBranches(res.data?.data || []);
      } catch {
        showError("Failed to fetch branches");
      }
    };

    fetchBranches();
  }, [isOpen, isAdmin]);

  useEffect(() => {
    if (!isOpen) return;

    if (!editingStaff) {
      setFormData(INITIAL_DATA);
      return;
    }

    setFormData({
      name: editingStaff.name || "",
      email: editingStaff.email || "",
      phoneNumber: editingStaff.phoneNumber || "",
      designation: editingStaff.designation || "",
      gender: editingStaff.gender || "",
      dateOfBirth: editingStaff.dateOfBirth
        ? editingStaff.dateOfBirth.slice(0, 10)
        : "",
      address: editingStaff.address || "",
      nationality: editingStaff.nationality || "Indian",
      assignedBranch: editingStaff.assignedBranch?._id || "",
    });
  }, [isOpen, editingStaff]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateForm = () => {
    const required = ["name", "email", "phoneNumber", "designation"];

    if (isAdmin) required.push("assignedBranch");

    const missing = required.find((f) => !formData[f]?.trim());

    if (missing) {
      showError(`Please fill ${missing}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phoneNumber: formData.phoneNumber.trim(),
        userType: "staff",
        designation: formData.designation.trim(),
        gender: formData.gender || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        address: formData.address || undefined,
        nationality: formData.nationality || undefined,
        assignedBranch: isAdmin
          ? formData.assignedBranch
          : admin?.assignedBranch,
      };

      const url = isEditMode
        ? `${process.env.REACT_APP_API_URL}/admins/update/${editingStaff._id}`
        : `${process.env.REACT_APP_API_URL}/admins/create`;

      const method = isEditMode ? "put" : "post";

      const res = await axios[method](url, payload);

      showSuccess(
        res?.data?.message ||
          (isEditMode
            ? "Staff updated successfully"
            : "Staff created successfully"),
      );

      onSuccess?.();
      onClose();
      setFormData(INITIAL_DATA);
    } catch (err) {
      showError(
        err?.response?.data?.message ||
          (isEditMode ? "Failed to update staff" : "Failed to create staff"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 h-full w-96 md:w-[500px] lg:w-[600px] bg-white z-50 shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "tween",
              duration: 0.35,
              ease: "easeInOut",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-[18px] border-b bg-brandAccentColor">
              <h2 className="text-lg font-bold text-primaryTextColor">
                {isEditMode ? "Edit Staff" : "Add New Staff"}
              </h2>
              <button onClick={onClose}>
                <X size={26} />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="px-6 pt-6 space-y-5 overflow-y-auto h-[calc(100vh-80px)]"
            >
              {isAdmin && (
                <Select
                  label="Branch"
                  name="assignedBranch"
                  value={formData.assignedBranch}
                  onChange={handleChange}
                  options={branches.map((b) => ({
                    label: b.branchName,
                    value: b._id,
                  }))}
                />
              )}

              <Input
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />

              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />

              <Input
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />

              <Input
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              />

              <Select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={GENDERS}
                optional
              />

              <Input
                label="Date of Birth"
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />

              <Textarea
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />

              <Input
                label="Nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
              />

              {/* Buttons */}
              <div className="flex gap-4 py-4 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border rounded-lg"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-primaryBtnColor text-white rounded-lg"
                >
                  {loading ? (
                    <Loader2 className="animate-spin mx-auto" />
                  ) : isEditMode ? (
                    "Update Staff"
                  ) : (
                    "Create Staff"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const Input = ({ label, optional, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <input
      {...props}
      required={!optional}
      className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
    />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <textarea
      {...props}
      rows={3}
      className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
    />
  </div>
);

const Select = ({ label, options, optional, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <select
      {...props}
      required={!optional}
      className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
    >
      <option value="">Select</option>
      {options.map((o) =>
        typeof o === "string" ? (
          <option key={o} value={o}>
            {o}
          </option>
        ) : (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ),
      )}
    </select>
  </div>
);

export default AddStaffDrawer;

// import axios from "axios";
// import { motion, AnimatePresence } from "framer-motion";
// import { Loader2, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import { showError, showSuccess } from "../../../utils/toastUtils";
// import { useAdminAuth } from "../../../context/AdminAuthContext";

// const INITIAL_DATA = {
//   name: "",
//   email: "",
//   phoneNumber: "",
//   password: "",
//   designation: "",
//   gender: "",
//   dateOfBirth: "",
//   address: "",
//   nationality: "Indian",
//   assignedBranch: "",
// };

// const GENDERS = ["male", "female", "other"];

// const AddStaffDrawer = ({ isOpen, onClose, onSuccess, editingStaff }) => {
//   const { isAdmin, admin } = useAdminAuth();
//   const isEditMode = Boolean(editingStaff);

//   const [formData, setFormData] = useState(INITIAL_DATA);
//   const [branches, setBranches] = useState([]);
//   const [loading, setLoading] = useState(false);

//   useEffect(() => {
//     if (!isOpen || !isAdmin) return;

//     const fetchBranches = async () => {
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API_URL}/branches/get-all`,
//         );

//         setBranches(res.data?.data || []);
//       } catch {
//         showError("Failed to fetch branches");
//       }
//     };

//     fetchBranches();
//   }, [isOpen, isAdmin]);

//   useEffect(() => {
//     if (!isOpen) return;

//     if (!editingStaff) {
//       setFormData(INITIAL_DATA);
//       return;
//     }

//     setFormData({
//       name: editingStaff.name || "",
//       email: editingStaff.email || "",
//       phoneNumber: editingStaff.phoneNumber || "",
//       password: "",
//       designation: editingStaff.designation || "",
//       gender: editingStaff.gender || "",
//       dateOfBirth: editingStaff.dateOfBirth
//         ? editingStaff.dateOfBirth.slice(0, 10)
//         : "",
//       address: editingStaff.address || "",
//       nationality: editingStaff.nationality || "Indian",
//       assignedBranch: editingStaff.assignedBranch?._id || "",
//     });
//   }, [isOpen, editingStaff]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((p) => ({ ...p, [name]: value }));
//   };

//   const validateForm = () => {
//     const required = ["name", "email", "phoneNumber", "designation"];

//     if (!isEditMode) required.push("password");
//     if (isAdmin) required.push("assignedBranch");

//     const missing = required.find((f) => !formData[f]?.trim());

//     if (missing) {
//       showError(`Please fill ${missing}`);
//       return false;
//     }
//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     setLoading(true);

//     try {
//       const payload = {
//         name: formData.name.trim(),
//         email: formData.email.trim().toLowerCase(),
//         phoneNumber: formData.phoneNumber.trim(),
//         userType: "staff",
//         designation: formData.designation.trim(),
//         gender: formData.gender || undefined,
//         dateOfBirth: formData.dateOfBirth || undefined,
//         address: formData.address || undefined,
//         nationality: formData.nationality || undefined,

//         assignedBranch: isAdmin
//           ? formData.assignedBranch
//           : admin?.assignedBranch,
//       };

//       if (formData.password) {
//         payload.password = formData.password;
//       }

//       const url = isEditMode
//         ? `${process.env.REACT_APP_API_URL}/admins/update/${editingStaff._id}`
//         : `${process.env.REACT_APP_API_URL}/admins/create`;

//       const method = isEditMode ? "put" : "post";

//       const res = await axios[method](url, payload);

//       showSuccess(
//         res?.data?.message ||
//           (isEditMode
//             ? "Staff updated successfully"
//             : "Staff created successfully"),
//       );

//       onSuccess?.();
//       onClose();
//       setFormData(INITIAL_DATA);
//     } catch (err) {
//       showError(
//         err?.response?.data?.message ||
//           (isEditMode ? "Failed to update staff" : "Failed to create staff"),
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <>
//           {/* Backdrop */}
//           <motion.div
//             className="fixed inset-0 bg-black/50 z-40"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             transition={{ duration: 0.25, ease: "easeOut" }}
//           />

//           {/* Drawer */}
//           <motion.div
//             className="fixed top-0 right-0 h-full w-96 md:w-[500px] lg:w-[600px] bg-white z-50 shadow-2xl"
//             initial={{ x: "100%" }}
//             animate={{ x: 0 }}
//             exit={{ x: "100%" }}
//             transition={{
//               type: "tween",
//               duration: 0.35,
//               ease: "easeInOut",
//             }}
//           >
//             {/* Header */}
//             <div className="flex items-center justify-between p-[18px] border-b bg-brandAccentColor">
//               <h2 className="text-lg font-bold text-primaryTextColor">
//                 {isEditMode ? "Edit Staff" : "Add New Staff"}
//               </h2>
//               <button onClick={onClose}>
//                 <X size={26} />
//               </button>
//             </div>

//             {/* Form */}
//             <form
//               onSubmit={handleSubmit}
//               className="px-6 pt-6 space-y-5 overflow-y-auto h-[calc(100vh-80px)]"
//             >
//               {isAdmin && (
//                 <Select
//                   label="Branch"
//                   name="assignedBranch"
//                   value={formData.assignedBranch}
//                   onChange={handleChange}
//                   options={branches.map((b) => ({
//                     label: b.branchName,
//                     value: b._id,
//                   }))}
//                 />
//               )}
//               <Input
//                 label="Full Name"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleChange}
//               />
//               <Input
//                 label="Email"
//                 type="email"
//                 name="email"
//                 value={formData.email}
//                 onChange={handleChange}
//               />
//               <Input
//                 label="Phone Number"
//                 name="phoneNumber"
//                 value={formData.phoneNumber}
//                 onChange={handleChange}
//               />

//               {!isEditMode && (
//                 <Input
//                   label="Password"
//                   type="password"
//                   name="password"
//                   value={formData.password}
//                   onChange={handleChange}
//                 />
//               )}

//               <Input
//                 label="Designation"
//                 name="designation"
//                 value={formData.designation}
//                 onChange={handleChange}
//               />

//               <Select
//                 label="Gender"
//                 name="gender"
//                 value={formData.gender}
//                 onChange={handleChange}
//                 options={GENDERS}
//                 optional
//               />

//               <Input
//                 label="Date of Birth"
//                 type="date"
//                 name="dateOfBirth"
//                 value={formData.dateOfBirth}
//                 onChange={handleChange}
//               />

//               <Textarea
//                 label="Address"
//                 name="address"
//                 value={formData.address}
//                 onChange={handleChange}
//               />

//               <Input
//                 label="Nationality"
//                 name="nationality"
//                 value={formData.nationality}
//                 onChange={handleChange}
//               />

//               {/* Buttons */}
//               <div className="flex gap-4 py-4 border-t sticky bottom-0 bg-white">
//                 <button
//                   type="button"
//                   onClick={onClose}
//                   className="flex-1 px-6 py-3 border rounded-lg"
//                 >
//                   Cancel
//                 </button>

//                 <button
//                   type="submit"
//                   disabled={loading}
//                   className="flex-1 px-6 py-3 bg-primaryBtnColor text-white rounded-lg"
//                 >
//                   {loading ? (
//                     <Loader2 className="animate-spin mx-auto" />
//                   ) : isEditMode ? (
//                     "Update Staff"
//                   ) : (
//                     "Create Staff"
//                   )}
//                 </button>
//               </div>
//             </form>
//           </motion.div>
//         </>
//       )}
//     </AnimatePresence>
//   );
// };
// const Input = ({ label, optional, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <input
//       {...props}
//       required={!optional}
//       className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
//     />
//   </div>
// );

// const Textarea = ({ label, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <textarea
//       {...props}
//       rows={3}
//       className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
//     />
//   </div>
// );

// const Select = ({ label, options, optional, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <select
//       {...props}
//       required={!optional}
//       className="w-full px-4 py-3 border rounded-lg outline-none focus:outline-none focus:ring-1 focus:ring-accentTextColor"
//     >
//       <option value="">Select</option>
//       {options.map((o) =>
//         typeof o === "string" ? (
//           <option key={o} value={o}>
//             {o}
//           </option>
//         ) : (
//           <option key={o.value} value={o.value}>
//             {o.label}
//           </option>
//         ),
//       )}
//     </select>
//   </div>
// );

// export default AddStaffDrawer;
